import { type SubmitSurveyRequest, isMultiFrequency } from '@bliks-io/contracts';

/**
 * Strips everything but digits and a single decimal separator (comma or dot)
 * from a duration input, so letters typed mid-number (e.g. "99876t5432")
 * never reach form state.
 */
export function sanitizeDurationInput(raw: string): string {
  const cleaned = raw.replace(/[^\d.,]/g, '');
  const firstSeparator = cleaned.search(/[.,]/);
  if (firstSeparator === -1) return cleaned;
  return (
    cleaned.slice(0, firstSeparator + 1) + cleaned.slice(firstSeparator + 1).replace(/[.,]/g, '')
  );
}

interface ValidationSettings {
  enableRatingAndComment: boolean;
  enableFrequencyMeasurement: boolean;
  enableDurationMeasurement: boolean;
}

function validateRatingSection(
  response: SubmitSurveyRequest,
  t: (key: string) => string
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!response.rating?.stars || response.rating.stars === 0) {
    errors.stars = t('processPage.ratingRequired');
  }

  return errors;
}

function validateFrequencySection(
  response: SubmitSurveyRequest,
  t: (key: string) => string
): Record<string, string> {
  const errors: Record<string, string> = {};

  const value = response.frequency?.value;
  if (!value || value.trim() === '') {
    errors.frequencyValue = t('processPage.surveyRequired');
    return errors;
  }

  // "mehrmals" options require an explicit positive occurrence count.
  if (isMultiFrequency(value)) {
    const count = response.frequency?.timesPerPeriod;
    if (count === undefined || !Number.isInteger(count) || count < 1) {
      errors.frequencyTimesPerPeriod = t('processPage.surveyRequired');
    }
  }

  return errors;
}

function validateDurationSection(
  response: SubmitSurveyRequest,
  t: (key: string) => string
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (
    response.duration?.value === undefined ||
    response.duration?.value === null ||
    isNaN(Number(response.duration.value)) ||
    response.duration.value <= 0
  ) {
    errors.durationValue = t('processPage.surveyRequired');
  }

  return errors;
}

export function validateSurvey(
  response: SubmitSurveyRequest,
  settings: ValidationSettings,
  t: (key: string) => string
): Record<string, string> {
  let errors: Record<string, string> = {};

  if (settings.enableRatingAndComment) {
    errors = { ...errors, ...validateRatingSection(response, t) };
  }

  if (settings.enableFrequencyMeasurement) {
    errors = { ...errors, ...validateFrequencySection(response, t) };
  }

  if (settings.enableDurationMeasurement) {
    errors = { ...errors, ...validateDurationSection(response, t) };
  }

  return errors;
}
